import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Link,
  Stack,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AppLayout from '../components/AppLayout';
import { formatReviewStatus, getReviewStatusColor, getReviewStatusFilterKey, REVIEW_STATUS_FILTER_OPTIONS } from '../utils/reviewStatus';
import { formatDateRange, utcMs } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

interface Review {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  employeeId: string;
  employeeName?: string;
  departmentNames?: string[];
  managerId?: string;
  managerName?: string;
  status: string | number;
  objectiveAverage?: number;
  behaviouralAverage?: number;
  combinedRating?: number;
  finalRating?: number | string;
  excludeRequestedAt?: string;
}

interface DepartmentAverageRow {
  departmentName: string;
  averageCombined: number;
  finalisedCount: number;
  previousAverageCombined?: number;
  delta?: number;
}

function normaliseFinalRating(value: number | string | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && (value === 1 || value === 2 || value === 3)) return value;
  if (typeof value === 'string') {
    const map: Record<string, number> = {
      DevelopmentRequired: 1,
      MeetsExpectation: 2,
      ExceedsExpectation: 3,
    };
    return map[value] ?? undefined;
  }
  return undefined;
}

export default function TeamReviewsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cycleId = searchParams.get('cycleId') ?? '';
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'employeeName' | 'reviewCycleName' | 'status' | 'managerName' | 'department'>('reviewCycleName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const filtersCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    apiService.get<ReviewCycle[]>('/review-cycles')
      .then((res) => {
        const active = (res.data ?? [])
          .filter((c) => c.isActive)
          .sort((a, b) => utcMs(a.startDate) - utcMs(b.startDate));
        setReviewCycles(active);
      })
      .catch(() => setReviewCycles([]));
  }, []);

  const handleCycleChange = (newCycleId: string) => {
    const next = new URLSearchParams(searchParams);
    if (newCycleId) {
      next.set('cycleId', newCycleId);
    } else {
      next.delete('cycleId');
    }
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const loadReviews = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const url = cycleId ? `/reviews/managed?cycleId=${encodeURIComponent(cycleId)}` : '/reviews/managed';
      const response = await apiService.get<Review[]>(url);
      setReviews(response.data);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('reviews')));
    } finally {
      setLoading(false);
    }
  }, [user?.id, cycleId]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const allDepartments = useMemo(() => {
    const set = new Set<string>();
    reviews.forEach((r) => r.departmentNames?.forEach((d) => set.add(d)));
    return Array.from(set).sort();
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    let filtered = [...reviews];
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.employeeName?.toLowerCase().includes(query) ||
          r.managerName?.toLowerCase().includes(query) ||
          r.departmentNames?.some((d) => d.toLowerCase().includes(query))
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => getReviewStatusFilterKey(r.status) === statusFilter.toLowerCase());
    }
    if (departmentFilter !== 'all') {
      filtered = filtered.filter((r) => r.departmentNames?.includes(departmentFilter));
    }
    return filtered;
  }, [reviews, searchTerm, statusFilter, departmentFilter]);

  const sortedReviews = useMemo(() => {
    return [...filteredReviews].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'employeeName') {
        cmp = (a.employeeName ?? '').localeCompare(b.employeeName ?? '');
      } else if (sortField === 'reviewCycleName') {
        cmp = (a.reviewCycleName ?? '').localeCompare(b.reviewCycleName ?? '');
      } else if (sortField === 'managerName') {
        cmp = (a.managerName ?? '').localeCompare(b.managerName ?? '');
      } else if (sortField === 'department') {
        cmp = (a.departmentNames?.[0] ?? '').localeCompare(b.departmentNames?.[0] ?? '');
      } else {
        cmp = formatReviewStatus(a.status).localeCompare(formatReviewStatus(b.status));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredReviews, sortField, sortDirection]);

  const paginatedReviews = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedReviews.slice(start, start + rowsPerPage);
  }, [sortedReviews, page, rowsPerPage]);

  const departmentAverages = useMemo<DepartmentAverageRow[]>(() => {
    const currentCycle = reviewCycles.find((c) => c.id === cycleId);
    const currentCycleName = currentCycle?.name;
    const currentCycleIndex = reviewCycles.findIndex((c) => c.id === cycleId);
    const latestCycleName = reviewCycles.length > 0 ? reviewCycles[reviewCycles.length - 1].name : undefined;
    const previousToLatestCycleName = reviewCycles.length > 1 ? reviewCycles[reviewCycles.length - 2].name : undefined;
    const trendCurrentCycleName = cycleId ? currentCycleName : latestCycleName;
    const previousCycleName = cycleId
      ? (currentCycleIndex > 0 ? reviewCycles[currentCycleIndex - 1].name : undefined)
      : previousToLatestCycleName;

    const grouped = new Map<string, { total: number; count: number }>();
    for (const review of reviews) {
      if (String(review.status).toLowerCase() !== 'finalised' && String(review.status) !== '6') continue;
      if (review.combinedRating == null) continue;
      const names = review.departmentNames?.length ? review.departmentNames : ['Not Assigned'];
      for (const name of names) {
        const row = grouped.get(name) ?? { total: 0, count: 0 };
        row.total += review.combinedRating;
        row.count += 1;
        grouped.set(name, row);
      }
    }

    const previousGrouped = new Map<string, { total: number; count: number }>();
    const trendCurrentGrouped = new Map<string, { total: number; count: number }>();
    if (trendCurrentCycleName) {
      for (const review of reviews) {
        if (String(review.status).toLowerCase() !== 'finalised' && String(review.status) !== '6') continue;
        if (review.combinedRating == null) continue;
        if (review.reviewCycleName !== trendCurrentCycleName) continue;
        const names = review.departmentNames?.length ? review.departmentNames : ['Not Assigned'];
        for (const name of names) {
          const row = trendCurrentGrouped.get(name) ?? { total: 0, count: 0 };
          row.total += review.combinedRating;
          row.count += 1;
          trendCurrentGrouped.set(name, row);
        }
      }
    }

    if (previousCycleName) {
      for (const review of reviews) {
        if (String(review.status).toLowerCase() !== 'finalised' && String(review.status) !== '6') continue;
        if (review.combinedRating == null) continue;
        if (review.reviewCycleName !== previousCycleName) continue;
        const names = review.departmentNames?.length ? review.departmentNames : ['Not Assigned'];
        for (const name of names) {
          const row = previousGrouped.get(name) ?? { total: 0, count: 0 };
          row.total += review.combinedRating;
          row.count += 1;
          previousGrouped.set(name, row);
        }
      }
    }

    return Array.from(grouped.entries())
      .map(([departmentName, values]) => ({
        departmentName,
        averageCombined: values.total / values.count,
        finalisedCount: values.count,
        previousAverageCombined: previousGrouped.get(departmentName)
          ? previousGrouped.get(departmentName)!.total / previousGrouped.get(departmentName)!.count
          : undefined,
        delta: previousGrouped.get(departmentName) && trendCurrentGrouped.get(departmentName)
          ? (trendCurrentGrouped.get(departmentName)!.total / trendCurrentGrouped.get(departmentName)!.count)
            - (previousGrouped.get(departmentName)!.total / previousGrouped.get(departmentName)!.count)
          : undefined,
      }))
      .sort((a, b) => b.averageCombined - a.averageCombined);
  }, [reviews, reviewCycles, cycleId]);

  const ratingDistributionData = useMemo(() => {
    const counts = { dev: 0, meets: 0, exceeds: 0 };
    for (const review of reviews) {
      if (String(review.status).toLowerCase() !== 'finalised' && String(review.status) !== '6') continue;
      const rating = normaliseFinalRating(review.finalRating);
      if (rating === 1) counts.dev += 1;
      else if (rating === 2) counts.meets += 1;
      else if (rating === 3) counts.exceeds += 1;
    }
    return [
      { band: 'Development Required', count: counts.dev },
      { band: 'Meets Expectation', count: counts.meets },
      { band: 'Exceeds Expectation', count: counts.exceeds },
    ];
  }, [reviews]);

  const scrollToFiltersCard = useCallback(() => {
    window.requestAnimationFrame(() => {
      filtersCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const departmentInsight = useMemo(() => {
    const withDelta = departmentAverages.filter((d) => d.delta != null);
    if (withDelta.length === 0) {
      return {
        summary: 'Not enough prior-cycle data yet to show department trend movement.',
        topGainDepartment: null as string | null,
      };
    }
    const improved = withDelta.filter((d) => (d.delta ?? 0) > 0).length;
    const declined = withDelta.filter((d) => (d.delta ?? 0) < 0).length;
    const stable = withDelta.length - improved - declined;
    const topGain = [...withDelta].sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))[0];
    return {
      summary: `${improved} improved, ${declined} declined, ${stable} stable.`,
      topGainDepartment: topGain.departmentName,
      topGainDelta: (topGain.delta ?? 0),
    };
  }, [departmentAverages]);

  const distributionInsight = useMemo(() => {
    const total = ratingDistributionData.reduce((sum, row) => sum + row.count, 0);
    if (total === 0) return 'No finalised ratings available for this scope yet.';
    const sorted = [...ratingDistributionData].sort((a, b) => b.count - a.count);
    const top = sorted[0];
    const pct = Math.round((top.count / total) * 100);
    return `${top.band} is most common (${top.count}/${total}, ${pct}%).`;
  }, [ratingDistributionData]);

  const trendHeaderLabel = cycleId
    ? 'Trend (vs previous cycle)'
    : 'Trend (latest vs prior)';

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
              <ArrowBackIcon />
            </IconButton>
            <VisibilityIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Your team and their teams
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Your team and their teams (view only). Everyone in your hierarchy – your direct reports and their teams. Use Reviews to Manage to take action on your direct reports.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Department Average Rating</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Average combined rating from finalised reviews only (your hierarchy). Trend compares selected cycle against previous cycle (or latest vs previous when all cycles are selected).
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {departmentInsight.topGainDepartment ? (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="body2">{departmentInsight.summary}</Typography>
                  <Typography variant="body2">Largest gain:</Typography>
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => {
                      setDepartmentFilter(departmentInsight.topGainDepartment!);
                      setPage(0);
                      scrollToFiltersCard();
                    }}
                  >
                    {departmentInsight.topGainDepartment}
                  </Link>
                  <Typography variant="body2">
                    ({departmentInsight.topGainDelta >= 0 ? '+' : ''}{departmentInsight.topGainDelta.toFixed(2)})
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2">{departmentInsight.summary}</Typography>
              )}
            </Alert>
            {departmentAverages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No finalised review ratings available yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Department</strong></TableCell>
                      <TableCell align="center"><strong>Finalised Reviews</strong></TableCell>
                      <TableCell align="center"><strong>Avg Combined</strong></TableCell>
                      <TableCell align="center"><strong>{trendHeaderLabel}</strong></TableCell>
                      <TableCell align="right"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentAverages.slice(0, 8).map((row) => (
                      <TableRow key={row.departmentName}>
                        <TableCell>{row.departmentName}</TableCell>
                        <TableCell align="center">{row.finalisedCount}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {row.averageCombined.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.delta == null ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Typography variant="body2" fontWeight={600} color={row.delta >= 0 ? 'success.main' : 'error.main'}>
                              {row.delta >= 0 ? '▲' : '▼'} {Math.abs(row.delta).toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Link
                            component="button"
                            variant="body2"
                            underline="hover"
                            onClick={() => {
                              setDepartmentFilter(row.departmentName);
                              setPage(0);
                              scrollToFiltersCard();
                            }}
                          >
                            View reviews
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card ref={filtersCardRef} variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Final Rating Distribution</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Finalised review outcomes by rating band for your selected team scope.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {distributionInsight}
            </Alert>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratingDistributionData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="band" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1976d2" name="Reviews" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search by employee name or manager..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 250 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  {REVIEW_STATUS_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => { setDepartmentFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="all">All departments</MenuItem>
                  {allDepartments.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel shrink id="team-reviews-cycle-filter-label">Review cycle</InputLabel>
                <Select
                  labelId="team-reviews-cycle-filter-label"
                  value={cycleId}
                  label="Review cycle"
                  onChange={(e) => handleCycleChange(e.target.value)}
                  displayEmpty
                  notched
                  renderValue={(value) => {
                    if (value === '') return `All active cycles (${reviewCycles.length})`;
                    const cycle = reviewCycles.find((c) => c.id === value);
                    if (!cycle) return value;
                    return cycle.startDate && cycle.endDate
                      ? `${cycle.name} (${formatDateRange(cycle.startDate, cycle.endDate)})`
                      : cycle.name;
                  }}
                >
                  <MenuItem value="">All active cycles ({reviewCycles.length})</MenuItem>
                  {reviewCycles.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                      {c.startDate && c.endDate && ` (${formatDateRange(c.startDate, c.endDate)})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchTerm.trim() !== '' || statusFilter !== 'all' || departmentFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchTerm.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => setSearchTerm('')}
                    size="small"
                    color="primary"
                  />
                )}
                {statusFilter !== 'all' && (() => {
                  const opt = REVIEW_STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter);
                  const label = opt?.label ?? formatReviewStatus(statusFilter);
                  return (
                    <Chip
                      label={`Status: ${label}`}
                      onDelete={() => setStatusFilter('all')}
                      size="small"
                      color={getReviewStatusColor(statusFilter)}
                    />
                  );
                })()}
                {departmentFilter !== 'all' && (
                  <Chip
                    label={`Department: ${departmentFilter}`}
                    onDelete={() => setDepartmentFilter('all')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer sx={{ borderTop: 0 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('employeeName')}>
                        <strong>Employee</strong>
                        {getSortIcon('employeeName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('department')}>
                        <strong>Department</strong>
                        {getSortIcon('department')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('reviewCycleName')}>
                        <strong>Cycle</strong>
                        {getSortIcon('reviewCycleName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                        <strong>Status</strong>
                        {getSortIcon('status')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('managerName')}>
                        <strong>Managed by</strong>
                        {getSortIcon('managerName')}
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <VisibilityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' ? 'No reviews match your search or filters.' : 'No reviews found.'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || statusFilter !== 'all' || departmentFilter !== 'all'
                              ? 'Try adjusting your search or filter criteria.'
                              : 'You don\'t have any reports with reviews in your hierarchy at this time'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedReviews.map((review) => (
                      <TableRow
                        key={review.id}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/reviews/${review.id}`)}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {review.employeeName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{review.departmentNames?.join(', ') || '—'}</TableCell>
                        <TableCell>{review.reviewCycleName || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip label={formatReviewStatus(review.status)} size="small" color={getReviewStatusColor(review.status)} />
                            {review.excludeRequestedAt && String(review.status).toLowerCase() !== 'excluded' && (
                              <Chip label="Exclude Requested" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{review.managerName || '—'}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/reviews/${review.id}`)}
                            color="primary"
                            title="View review"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedReviews.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            />
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
